export const meta = {
  title: 'Introducing Swing Choreo',
  summary:
    'A motion model for whole performances: chained actions, hands, faces, and multiple people from one prompt.',
  author: 'Cartwheel Team',
  category: 'research',
  date: '2026-10-09',
  hero: {
    type: 'video',
    cdnKey: '01_SwingChoreo_intro.mp4',
  },
  slug: 'swing-choreo',
};

<ArticleVideo
  cdnKey="choreo-r29-AE-v05-4K30.mp4"
  alt="Swing Choreo launch film: single characters, pairs, and groups performing prompted sequences"
/>

Swing Choreo is our new model for generating human motion. You describe a performance the way you would describe it to an actor, one beat after another, and Choreo performs all of it: each action in order, the hands and the face, every person in the scene, moving where you asked them to go.

It is the most capable model we have ever produced. In blind comparisons, professional animators preferred its output to our previous generation 88% of the time.

## What it does

### Whole sequences from one prompt

Most text-to-motion models learn from short clips paired with short captions. Ask for three things and you tend to get one of them, or an average of all three. Choreo reads the prompt as a sequence and performs it in order, with transitions that carry weight and timing from one action into the next.

<ArticleVideo
  cdnKey="02_sequences.mp4"
  caption="“Football player running, then slams football on the ground and celebrates.”"
/>

### Hands and faces

The body is only part of a performance. Choreo generates finger articulation and facial motion alongside the body, so a prompt can ask for a hand shape, a gesture, or a laugh and get it.

<ArticleVideo
  cdnKey="03_hands.mp4"
  caption="Two characters play rock, paper, scissors. Hand shapes come directly from the prompt."
/>

### More than one person

Choreo generates several characters at once, each with their own action, aware of each other. They face each other when they talk, react when someone moves, and stay in time when they dance together.

<ArticleVideo
  cdnKey="04_multi-char.mp4"
  caption="Four people in conversation, generated together from one prompt."
/>

### Direction in space

You can tell characters where to go, in the world or relative to the camera. Left means camera left.

<ArticleVideo
  cdnKey="05_directions.mp4"
  caption="“Run to the left and then to the right.”"
/>

## How it compares

We evaluate models the way they are used. Professional animators see two clips generated from the same prompt, without knowing which model made which, and pick the one they would rather use. Against Swing Sketch, our previous generation, Choreo wins 88% of those comparisons.

<WinRateChart
  model="Swing Choreo"
  opponent="Swing Sketch"
  rows={[
    { label: 'Overall', win: 88, tie: 4, loss: 8 },
    { label: 'Multi-action sequences', win: 92, tie: 3, loss: 5 },
    { label: 'Hands and fingers', win: 94, tie: 2, loss: 4 },
    { label: 'Multiple people', win: 100, tie: 0, loss: 0 },
    { label: 'Spatial direction', win: 86, tie: 5, loss: 9 },
    { label: 'Single-action prompts', win: 71, tie: 14, loss: 15 },
  ]}
  caption="Blind pairwise preference from professional animators. Clip order and position were randomized."
/>

The gap is largest where the prompt asks for the most: several actions, several people, or detailed hands. On simple, single-action prompts the two models are closer, which is what you would expect. Sketch was built for exactly that case and still performs better for fantastical motions.

Here is what that looks like across the capabilities that matter most in production.

<ComparisonTable
  columns={['Swing Choreo', 'Swing Sketch', 'Open research models', 'Commercial tools']}
  rows={[
    {
      capability: 'Multi-action sequences',
      detail: 'Several actions, performed in order, from one prompt',
      values: ['yes', 'partial', 'partial', 'partial'],
    },
    {
      capability: 'Finger articulation',
      detail: 'Hand shapes and gestures from the prompt',
      values: ['yes', 'partial', 'no', 'partial'],
    },
    {
      capability: 'Facial motion',
      detail: 'Expression generated with the body',
      values: ['yes', 'no', 'no', 'no'],
    },
    {
      capability: 'Multiple interacting people',
      detail: 'Separate actions, aware of each other',
      values: ['yes', 'no', 'no', 'no'],
    },
    {
      capability: 'World and camera direction',
      detail: '“Walk toward camera”, “run to the left”',
      values: ['yes', 'partial', 'no', 'partial'],
    },
    {
      capability: 'Adherence on long prompts',
      detail: 'Follows compound, multi-clause descriptions',
      values: ['yes', 'yes', 'no', 'partial'],
    },
    {
      capability: 'Motion variety',
      detail: 'Distinct, usable takes for the same prompt',
      values: ['yes', 'yes', 'partial', 'partial'],
    },
    {
      capability: 'Results in seconds',
      detail: 'For fast blocking and iteration',
      values: ['no', 'yes', 'yes', 'yes'],
    },
  ]}
  caption="Capabilities as tested by our team in October 2026."
/>

## More compute, better motion

Choreo is slower than Sketch. A generation takes a few minutes instead of a few seconds, and that time is spent on quality. The same model can be run at higher or lower quality, which traces out a curve: more compute buys better motion, with diminishing returns past the default setting.

<ParetoChart
  yLabel="Human preference (Elo)"
  xLabel="Time per generation (log scale)"
  xScale="log"
  xTicks={[1, 5, 10, 100]}
  xTickLabels={['1s', '5s', '10s', '100s']}
  yTicks={[900, 1000, 1100, 1200, 1300]}
  frontier="Swing"
  points={[
    { label: 'Swing Sketch', x: 3, y: 1010, series: 'Swing', labelPosition: 'below' },
    { label: 'Choreo, 25%', x: 12, y: 1180, series: 'Swing', labelPosition: 'left' },
    { label: 'Choreo, 50%', x: 25, y: 1235, series: 'Swing', labelPosition: 'left' },
    { label: 'Choreo, 100%', x: 60, y: 1272, series: 'Swing', labelPosition: 'above' },
    { label: 'Open model A', x: 6, y: 930 },
    { label: 'Open model B', x: 30, y: 985 },
    { label: 'Commercial model', x: 52, y: 1045 },
  ]}
  caption="Each point is one model and setting. The Swing models define the frontier at every time budget we measured."
/>

Most of Choreo's gains came from data selection and post-training. Two things usually trade off in motion generation: following the prompt closely, and producing motion that looks physically right. Push a model toward one and the other tends to slip. Each stage of post-training moved Choreo up on both at once.

<ParetoChart
  yLabel="Prompt adherence (%)"
  xLabel="Motion quality score"
  xTicks={[50, 60, 70, 80, 90]}
  yTicks={[40, 50, 60, 70, 80, 90]}
  frontier="Swing Choreo (illustrated training)"
  points={[
    { label: 'Pretrained', x: 62, y: 47, series: 'Swing Choreo (illustrated training)', labelPosition: 'right' },
    { label: 'Fine-tuned', x: 70, y: 66, series: 'Swing Choreo (illustrated training)', labelPosition: 'left' },
    { label: 'Preference-tuned', x: 78, y: 77, series: 'Swing Choreo (illustrated training)', labelPosition: 'left' },
    { label: 'Swing Choreo', x: 84, y: 86, series: 'Swing Choreo (illustrated training)', labelPosition: 'left' },
    { label: 'Swing Sketch', x: 75, y: 71, labelPosition: 'right' },
    { label: 'Open model A', x: 54, y: 41 },
    { label: 'Open model B', x: 57, y: 50, labelPosition: 'above' },
    { label: 'Commercial model', x: 66, y: 56, labelPosition: 'right' },
  ]}
  caption="Prompt adherence and motion quality (benchmark coming soon) on held-out prompts, measured after each stage of training."
/>

## The model factory

Choreo and all our motion systems are not a single research bet. They come out of a system we have spent the last year building: a loop that runs from capturing motion, through training and evaluation, back to capturing more. Each pass through the loop produces a better model and tells us exactly what to capture next.

### Data

Every capability in Choreo starts as data. Hands need finger-level capture. Faces need facial capture recorded in sync with the body. Multi-person scenes need several performers captured together, in the same space, reacting to each other. None of that exists in public datasets at useful scale, so we built (and are building!) the pipeline to produce it.

Raw capture is only half the work. Each recording is segmented into individual actions and described in time-aligned language, so the model can learn what “then” means. Positions are annotated relative to the world and to the camera, which is where spatial direction comes from. The result is the largest structured human motion dataset we know of: tens of thousands of hours from hundreds upon hundreds of performers.

### Training

In August we published [scaling laws for human motion](/blog/scaling-laws). That work is now how we plan our big runs and allocate resources. Before committing to a large run, we fit small runs, predict where the large one will land, and size the model and data to match. These predictions helped inform Choreo.

The same infrastructure lets us run many experiments in parallel and reproduce any of them later. Over Choreo's development we ran thousands of training experiments. Most were small. A few mattered a great deal, we had a few YOLO runs in there too.

### Post-training

Post-training is where Choreo became Choreo. We worked to build it around the people who will use it.

The pairwise comparisons we use for evaluation double as training signal. Animators judge pairs of generations, those judgments become preference data, and the model is tuned toward what they choose. Then we evaluate again. Over the course of Choreo's post-training, animators made many of these comparisons (and our physics models helped too).

Evaluation also tells us where the model fails, and those failures go back to the capture stage. If the model struggles with two people passing an object between them, we fix Comic (our capture model) by augmenting training data and identifying long-tail weaknesses and then we can improve the training data for our generation systems. The loop closes when we put eyes on data and really infuse physics into the kinematics of Choreo, this is something you can't really do by scraping more data.

### Evaluation

Every candidate model runs through the same rigorous (but hopefully fast) evaluation process. We have automatic checks for physical errors like foot sliding, ground penetration, and broken contact, then blind review by animators. We have faithfulness, embedding, and many other proxy rewards that help improve overall motion fidelity.

## Measuring motion

Human motion generation does not yet have a good public benchmark. The standard ones measure short, single-action clips, mostly through statistics that correlate poorly with what an animator would accept. They cannot tell you whether a model followed a four-part prompt, got the fingers right, or kept two people from walking through each other.

We have been building one that can (paper and open source release coming soon!). It covers sequences, hands, faces, multiple people, and spatial direction, and it scores physical plausibility alongside adherence. We will publish it, with the data and evaluation code, in the coming weeks.

Early results are lopsided. On the preliminary version, Choreo scores 61 out of 100. The best system we have tested from anyone else scores 38.

<BenchmarkPreview
  max={100}
  rows={[
    { label: 'Swing Choreo', value: 61 },
    { label: 'System A', value: 38 },
    { label: 'System B', value: 22.5 },
    { label: 'System C', value: 3.2 },
    { label: 'System D', value: 2.4 },
  ]}
  caption="Preliminary results. Other systems are anonymized until the benchmark is published."
/>

## Choreo and Sketch

Choreo is the model to use when the performance matters: final animation, complex scenes, hands and faces, several characters at once. When you need blocking in seconds, Swing Sketch is still there and still fast. Most people will use both.

Swing Choreo is available today in Performance Generator.

<SwingChoreoCTA />
